import { API, LOGO_URL } from "@/lib/api";
import { CtaButton, Kicker } from "@/components/landing/ui";

export const BeforeAfter = ({ item, compact }) => (
  <figure data-testid={`proof-item-${item.id}`}>
    <div className="grid grid-cols-2 gap-1 bg-white">
      {[["Before", item.before_photo_id], ["After", item.after_photo_id]].map(([label, pid]) => (
        <div key={label} className="relative">
          <img src={`${API}/photos/${pid}`} alt={`${item.title} — ${label}`} loading="lazy" className="w-full aspect-[4/5] sm:aspect-[4/3] object-cover bg-gray-100" />
          <span className={`absolute bottom-2.5 left-2.5 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 ${label === "After" ? "bg-[#c19a6b] text-white" : "bg-black/75 text-white"}`}>
            {label}
          </span>
        </div>
      ))}
    </div>
    {!compact && (
      <figcaption className="pt-4">
        {item.furniture_type && <p className="text-[10px] uppercase tracking-[0.25em] text-[#c19a6b] font-bold">{item.furniture_type}</p>}
        <p className="font-display text-xl mt-1">{item.title}</p>
        {item.caption && <p className="text-sm text-gray-500 leading-relaxed mt-1.5">{item.caption}</p>}
      </figcaption>
    )}
  </figure>
);

export const ProofGallery = ({ gallery }) => {
  if (!gallery?.length) return null;
  return (
    <section className="py-20 sm:py-28" data-testid="proof-gallery">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl">
          <Kicker>Real transformations</Kicker>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">See what's possible.</h2>
          <p className="text-gray-600 text-lg leading-relaxed mt-6">
            Revival isn't about turning your furniture into something completely different. It's about making the furniture you already love look beautiful again.
          </p>
        </div>
        <div className={`grid gap-10 sm:gap-12 mt-14 ${gallery.length === 1 ? "max-w-3xl" : "sm:grid-cols-2"}`}>
          {gallery.slice(0, 6).map((g) => <BeforeAfter key={g.id} item={g} />)}
        </div>
      </div>
    </section>
  );
};

export const PromiseSection = () => (
  <section className="bg-black text-white py-20 sm:py-28" data-testid="promise-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-3xl">
        <span className="inline-block border border-[#c19a6b] text-[#c19a6b] text-[10px] uppercase tracking-[0.3em] font-bold px-4 py-2">The ARK Revival Promise</span>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mt-8">
          We won't recommend Revival just to make a sale.
        </h2>
        <p className="text-white/70 text-lg leading-relaxed mt-8">
          We'll review your furniture and what you're hoping to accomplish. If we don't believe resurfacing will create a meaningful
          improvement — or if another service would be more appropriate — we'll tell you before you invest in it.
        </p>
      </div>
    </div>
  </section>
);

const NEXT = [
  { t: "We review your furniture", d: "We'll look at your project, goals and furniture to determine whether Revival appears to be a good fit." },
  { t: "We recommend the right service", d: "If Revival makes sense, we'll explain why. If restoration or another option would be better, we'll tell you." },
  { t: "You get your options", d: "If you'd like to proceed, we'll walk you through personalized Revival options for your furniture." },
];

export const NextStepsSection = () => (
  <section className="py-20 sm:py-28 border-t border-gray-100" data-testid="next-steps-section">
    <div className="max-w-6xl mx-auto px-6">
      <Kicker>What happens next</Kicker>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">Simple from here.</h2>
      <div className="grid md:grid-cols-3 gap-10 md:gap-8 mt-14">
        {NEXT.map((s, i) => (
          <div key={s.t} className="border-t border-gray-200 pt-6">
            <span className="font-display text-5xl text-[#c19a6b]/70">{i + 1}</span>
            <h3 className="font-display text-xl mt-4 leading-snug">{s.t}</h3>
            <p className="text-gray-600 text-[15px] leading-relaxed mt-3">{s.d}</p>
          </div>
        ))}
      </div>
      <div className="mt-14">
        <CtaButton testId="footer-cta" className="w-full sm:w-auto" />
      </div>
    </div>
  </section>
);

export const LandingFooter = () => (
  <footer className="border-t border-gray-100 py-12" data-testid="landing-footer">
    <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-5">
      <img src={LOGO_URL} alt="ARK Furniture" className="h-9 opacity-80" />
      <p className="text-xs text-gray-400 tracking-wide text-center">
        © {new Date().getFullYear()} ARK Furniture · Serving the GTA ·{" "}
        <a href="https://arkfurniture.ca" target="_blank" rel="noreferrer" className="hover:text-black underline underline-offset-4 transition-colors duration-200" data-testid="footer-site-link">
          arkfurniture.ca
        </a>
      </p>
    </div>
  </footer>
);
